// The Salesperson cell's popover body: a scrollable list of active, non-client persons plus an
// Unassigned item that clears the field. Saves on selection with no Update step (spec 008 §2.1).
//
// Options load on FIRST OPEN via useForecastFetchQuery rather than riding the page query: that query
// already loads every project up front and both list hosts declare it, so widening it would cost a
// bigger payload on every page load for data most sessions never open. AGENTS.md sanctions
// useForecastFetchQuery for exactly this — an imperative fetch triggered by a user action.
//
// No search field, deliberately (spec 008 resolved OQ-1): this matches the Create Opportunity modal's
// salesperson dropdown, so the two entry points stay identical. A typeahead is a follow-up if beta asks.
import React, {useEffect, useState} from 'react';
import {graphql} from 'react-relay';
import {Avatar, Text} from '@forecast-it/design-system';
import Styled from 'styled-components';
import Util from '../../../shared/util/util';
import UpdateProjectMutation from '../../../../mutations/update_project_mutation.modern';
import {useForecastFetchQuery} from '../../../shared/hooks/useForecastFetchQuery';
import {profilePicSrc} from '../../../../directApi';
import type {OpportunityNode} from '../../opportunityTypes';

// The same query the Create Opportunity modal runs for its salesperson picker, so the two entry
// points offer the same set of people.
const salespersonOptionsQuery = graphql`
	query SalespersonEditor_Query {
		viewer {
			actualPersonId
			component(name: "opportunity_salesperson_editor")
			company {
				allPersons(first: 10000, onlyActive: true, excludeClientUsers: true) {
					edges {
						node {
							id
							fullName
							active
							profilePictureId
						}
					}
				}
			}
		}
	}
`;

type PersonOption = {id: string; fullName: string | null; active: boolean | null; profilePictureId: string | null};
type QueryData = {viewer: {company: {allPersons: {edges: Array<{node: PersonOption}>}}}};

export interface SalespersonEditorProps {
	node: OpportunityNode;
	// The page's retry() — the list refetches after a save (D13).
	onCommit: () => void;
	// Dismisses the popover.
	close: () => void;
	// Localized "Unassigned" label for the clear item.
	unassignedLabel: string;
	// Shown while the person list is still loading, in place of the options.
	loadingLabel: string;
	// Shown when the query failed, so a dead popover is distinguishable from a slow one.
	failedLabel: string;
}

const List = Styled.div`
	display: flex;
	flex-direction: column;
	width: 280px;
	max-height: 320px;
	overflow-y: auto;
	padding: 4px;
`;
const Option = Styled.button<{selected: boolean}>`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	padding: 8px;
	border: none;
	border-radius: 4px;
	background: ${({selected}) => (selected ? '#eef0ff' : 'none')};
	cursor: pointer;
	text-align: left;
	&:hover {
		background: #eef0ff;
	}
`;
const Loading = Styled.span`
	padding: 8px;
	color: #71717a;
`;
const Divider = Styled.span`
	height: 1px;
	margin: 4px 0;
	background: #e4e4e7;
`;

export const SalespersonEditor = ({
	node,
	onCommit,
	close,
	unassignedLabel,
	loadingLabel,
	failedLabel,
}: SalespersonEditorProps): JSX.Element => {
	const {data, fetch, loading} = useForecastFetchQuery<QueryData, Record<string, never>>(salespersonOptionsQuery);

	// The hook clears `loading` on a rejection and leaves `data` null, and surfaces no error of its own,
	// so a failure is otherwise indistinguishable from a slow connection - the popover would sit on
	// "Loading..." forever with no options, no message and no way out. Track the failure ourselves.
	const [failed, setFailed] = useState(false);

	// Once per open: the popover content mounts when it opens and unmounts when it closes, so this runs
	// again each time the cell is opened. That is deliberate rather than cached - a colleague added or
	// deactivated since the last open should show up, and this is one small query on an explicit click.
	useEffect(() => {
		fetch(undefined, undefined, () => setFailed(true));
		// Intentionally mount-only: fetch is re-created on every render by the hook, so depending on it
		// would refetch in a loop. web-front does not enable react-hooks/exhaustive-deps.
	}, []);

	const currentId = node.salesperson?.id ?? null;
	const persons = (data?.viewer.company.allPersons.edges ?? []).map(edge => edge.node).filter(person => person.active);

	const select = (salespersonId: string | null) => {
		// Re-picking the current value would commit a no-op mutation and refetch the whole list.
		if (salespersonId === currentId) {
			close();
			return;
		}
		close();
		// null clears the salesperson: service-company's ProjectExtensionRequest.fromProjectRequest reads
		// null / <= 0 as "clear it".
		Util.CommitMutation(UpdateProjectMutation, {project: {id: node.id}, salespersonId}, onCommit);
	};

	// Until the options arrive, the list would be nothing but the Unassigned row - and clicking it
	// commits, so a quick click on a slow connection silently cleared the salesperson instead of
	// picking one. Show the loading state alone until there is something real to choose between.
	if (failed) {
		return (
			<List data-cy="salesperson-editor">
				<Loading data-cy="salesperson-failed">{failedLabel}</Loading>
			</List>
		);
	}
	if (loading || !data) {
		return (
			<List data-cy="salesperson-editor">
				<Loading data-cy="salesperson-loading">{loadingLabel}</Loading>
			</List>
		);
	}

	return (
		<List data-cy="salesperson-editor">
			{persons.map(person => (
				<Option
					key={person.id}
					type="button"
					selected={person.id === currentId}
					aria-selected={person.id === currentId}
					data-cy={`salesperson-option-${person.id}`}
					data-testid={`salesperson-option-${person.id}`}
					onClick={() => select(person.id)}
				>
					{/* data-private: profile-picture endpoints are authenticated, so LogRocket must strip the src. */}
					<Avatar
						size="s"
						name={person.fullName ?? ''}
						src={person.profilePictureId ? (profilePicSrc(person.profilePictureId) as string) : undefined}
						data-private
					/>
					<Text>{person.fullName}</Text>
				</Option>
			))}
			<Divider />
			<Option
				type="button"
				selected={currentId === null}
				aria-selected={currentId === null}
				data-cy="salesperson-option-none"
				data-testid="salesperson-option-none"
				onClick={() => select(null)}
			>
				<Text>{unassignedLabel}</Text>
			</Option>
		</List>
	);
};

export default SalespersonEditor;
